import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Button, CircularProgress, Stack, Chip,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Divider, useMediaQuery, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import api from '../api';

const INTERVIEW_TYPES = [
  { value: 'Informal Chat',        emoji: '💬', company: null,     description: 'Relaxed conversation about background & interests',       color: '#64748b' },
  { value: 'Amazon Phone Screen',  emoji: '📱', company: 'Amazon', description: 'LP-focused + 1-2 technical questions (~45 min)',           color: '#f97316' },
  { value: 'Amazon Loop',          emoji: '🔄', company: 'Amazon', description: 'Bar Raiser LP deep-dive (~60 min)',                        color: '#ef4444' },
  { value: 'Google RRK',           emoji: '🎓', company: 'Google', description: 'Role-Related Knowledge technical assessment',              color: '#4285F4' },
  { value: 'Google GCA',           emoji: '🧩', company: 'Google', description: 'General Cognitive Ability — structured problem solving',   color: '#0ea5e9' },
  { value: 'Google G&L',           emoji: '🌟', company: 'Google', description: 'Googleyness & Leadership values',                         color: '#34a853' },
  { value: 'Behavioural',          emoji: '⭐', company: null,     description: 'Competency-based STAR interview',                          color: '#8b5cf6' },
  { value: 'Technical',            emoji: '⚙️', company: null,     description: 'Technical deep-dive from job description',                color: '#06b6d4' },
];

const PROVIDERS = {
  openai:    { label: 'OpenAI',         color: '#10a37f', models: [{ id: 'gpt-4o', label: 'GPT-4o' }, { id: 'gpt-4o-mini', label: 'GPT-4o Mini' }, { id: 'gpt-4-turbo', label: 'GPT-4 Turbo' }] },
  anthropic: { label: 'Anthropic',      color: '#d97706', models: [{ id: 'claude-opus-4-6', label: 'Claude Opus 4.6' }, { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' }, { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }] },
  gemini:    { label: 'Google Gemini',  color: '#4285f4', models: [{ id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' }, { id: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' }] },
  bedrock:   { label: 'Amazon Bedrock', color: '#FF9900', models: [{ id: 'global.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5' }] },
};

const selectSx = {
  color: 'var(--tx-md)', fontSize: '0.82rem',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--bd-lo)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.5)' },
  '& .MuiSelect-icon': { color: 'var(--tx-dim)' },
  bgcolor: 'var(--bg-surf)',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatTimer(s) {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// ─── Interview view ────────────────────────────────────────────────────────────
function InterviewView({ session, onEnd }) {
  const [messages, setMessages] = useState(session.transcript || []);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [started, setStarted] = useState(session.transcript && session.transcript.length > 0);
  const [srSupported, setSrSupported] = useState(true);
  const [textInput, setTextInput] = useState('');

  const recognitionRef = useRef(null);
  const transcriptRef = useRef(null);
  const timerRef = useRef(null);
  const synth = window.speechSynthesis;

  // Timer
  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [messages, liveText]);

  // Setup SpeechRecognition
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSrSupported(false); return; }
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = 'en-US';
    r.onstart = () => setIsListening(true);
    r.onend = () => { setIsListening(false); setLiveText(''); };
    r.onerror = () => { setIsListening(false); setLiveText(''); };
    r.onresult = (event) => {
      const t = Array.from(event.results).map(r => r[0].transcript).join('');
      setLiveText(t);
      if (event.results[event.results.length - 1].isFinal) {
        setLiveText('');
        handleUserTurn(t);
      }
    };
    recognitionRef.current = r;
  }, [messages]); // rebind so handleUserTurn closure captures fresh messages

  const speak = useCallback((text) => {
    synth.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.95;
    utt.pitch = 1.0;
    const voices = synth.getVoices();
    const preferred = voices.find(v => v.lang.startsWith('en') && (v.name.includes('Daniel') || v.name.includes('Alex') || v.name.includes('Samantha') || v.name.includes('Google') || v.name.includes('en-US')));
    if (preferred) utt.voice = preferred;
    utt.onstart = () => setIsSpeaking(true);
    utt.onend = () => setIsSpeaking(false);
    utt.onerror = () => setIsSpeaking(false);
    synth.speak(utt);
  }, []);

  const saveTranscript = useCallback(async (msgs) => {
    try {
      await api.put(`/api/rehearsals/${session.id}`, { transcript: msgs });
    } catch {}
  }, [session.id]);

  const handleUserTurn = useCallback(async (userText) => {
    const isOpener = !userText?.trim();
    const updatedMsgs = isOpener ? [] : [...messages, { role: 'user', content: userText.trim(), ts: new Date().toISOString() }];
    if (!isOpener) setMessages(updatedMsgs);
    setIsThinking(true);

    try {
      const res = await api.post(`/api/rehearsals/${session.id}/turn`, {
        messages: updatedMsgs.map(m => ({ role: m.role, content: m.content })),
        interview_type: session.interview_type,
        context_docs: session.context_docs,
        provider: session.provider,
        model: session.model,
      });
      if (res.data.success) {
        const aiMsg = { role: 'assistant', content: res.data.reply, ts: new Date().toISOString() };
        const finalMsgs = [...updatedMsgs, aiMsg];
        setMessages(finalMsgs);
        setIsThinking(false);
        speak(res.data.reply);
        await saveTranscript(finalMsgs);
      }
    } catch {
      setIsThinking(false);
    }
  }, [messages, session, speak, saveTranscript]);

  // Kick off first AI turn once on mount
  const firedRef = useRef(false);
  useEffect(() => {
    if (!firedRef.current && !started) {
      firedRef.current = true;
      setStarted(true);
      (async () => {
        setIsThinking(true);
        try {
          const res = await api.post(`/api/rehearsals/${session.id}/turn`, {
            messages: [],
            interview_type: session.interview_type,
            context_docs: session.context_docs,
            provider: session.provider,
            model: session.model,
          });
          if (res.data.success) {
            const aiMsg = { role: 'assistant', content: res.data.reply, ts: new Date().toISOString() };
            setMessages([aiMsg]);
            setIsThinking(false);
            speak(res.data.reply);
            try { await api.put(`/api/rehearsals/${session.id}`, { transcript: [aiMsg] }); } catch {}
          }
        } catch { setIsThinking(false); }
      })();
    }
  }, []); // eslint-disable-line

  const startListening = () => {
    if (isSpeaking || isThinking || isListening) return;
    synth.cancel();
    setIsSpeaking(false);
    try { recognitionRef.current?.start(); } catch {}
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch {}
  };

  const handleEndSession = async () => {
    synth.cancel();
    clearInterval(timerRef.current);
    try { await api.put(`/api/rehearsals/${session.id}`, { transcript: messages, status: 'completed' }); } catch {}
    onEnd(messages);
  };

  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    handleUserTurn(textInput.trim());
    setTextInput('');
  };

  const typeMeta = INTERVIEW_TYPES.find(t => t.value === session.interview_type) || INTERVIEW_TYPES[6];
  const statusText = isThinking ? 'Thinking...' : isSpeaking ? 'Interviewer speaking...' : isListening ? 'Listening...' : 'Your turn';
  const orbColor = isThinking ? '#6366f1' : isSpeaking ? '#818cf8' : isListening ? '#ef4444' : '#334155';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0a0f1e' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#e2e8f0' }}>{session.title}</Typography>
          <Chip label={session.interview_type} size="small"
            sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${typeMeta.color}22`, color: typeMeta.color, border: `1px solid ${typeMeta.color}44`, '& .MuiChip-label': { px: 0.75 } }} />
          {session.provider && (
            <Chip label={`${PROVIDERS[session.provider]?.label || session.provider} · ${PROVIDERS[session.provider]?.models.find(m => m.id === session.model)?.label || session.model}`} size="small"
              sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: `${PROVIDERS[session.provider]?.color || '#64748b'}18`, color: PROVIDERS[session.provider]?.color || '#64748b', border: `1px solid ${PROVIDERS[session.provider]?.color || '#64748b'}33`, '& .MuiChip-label': { px: 0.75 } }} />
          )}
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace' }}>{formatTimer(elapsed)}</Typography>
        <Button size="small" onClick={handleEndSession}
          sx={{ fontSize: '0.72rem', color: '#ef4444', border: '1px solid #ef444433', borderRadius: 1.5, px: 1.5, py: 0.4, textTransform: 'none', '&:hover': { bgcolor: '#ef444411' } }}>
          End Session
        </Button>
      </Box>

      {/* Transcript */}
      <Box ref={transcriptRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {messages.length === 0 && !isThinking && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: '#475569', fontSize: '0.85rem', fontStyle: 'italic' }}>Starting interview...</Typography>
          </Box>
        )}
        {messages.map((msg, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'assistant' ? 'flex-start' : 'flex-end' }}>
            <Box sx={{
              maxWidth: '72%',
              bgcolor: msg.role === 'assistant' ? 'rgba(99,102,241,0.12)' : 'rgba(51,65,85,0.6)',
              border: `1px solid ${msg.role === 'assistant' ? 'rgba(99,102,241,0.25)' : 'rgba(100,116,139,0.3)'}`,
              borderRadius: msg.role === 'assistant' ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
              px: 2, py: 1.25,
            }}>
              <Typography sx={{ fontSize: '0.65rem', color: msg.role === 'assistant' ? '#818cf8' : '#94a3b8', fontWeight: 700, mb: 0.4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {msg.role === 'assistant' ? 'Interviewer' : 'You'}
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', color: '#e2e8f0', lineHeight: 1.65 }}>{msg.content}</Typography>
            </Box>
          </Box>
        ))}
        {isThinking && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box sx={{ bgcolor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '4px 14px 14px 14px', px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} sx={{ color: '#6366f1' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#6366f1' }}>Thinking...</Typography>
            </Box>
          </Box>
        )}
        {liveText && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ maxWidth: '72%', bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '14px 4px 14px 14px', px: 2, py: 1.25 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 700, mb: 0.4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Listening...</Typography>
              <Typography sx={{ fontSize: '0.88rem', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.65 }}>{liveText}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Audio controls */}
      <Box sx={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: '#0d1526', px: 3, py: 2.5 }}>
        <Stack direction="column" alignItems="center" spacing={1.5}>
          {/* Orb + status */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {(isSpeaking || isThinking) && (
              <CircularProgress size={72} thickness={1.5} sx={{ color: orbColor, position: 'absolute', opacity: 0.4 }} />
            )}
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%', bgcolor: orbColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background-color 0.3s',
              boxShadow: isSpeaking ? `0 0 20px ${orbColor}66` : isListening ? '0 0 20px #ef444466' : 'none',
            }}>
              {isSpeaking ? <VolumeUpIcon sx={{ color: '#fff', fontSize: 24 }} />
                : isThinking ? <CircularProgress size={20} thickness={3} sx={{ color: '#fff' }} />
                : isListening ? <MicIcon sx={{ color: '#fff', fontSize: 24 }} />
                : <RecordVoiceOverIcon sx={{ color: '#64748b', fontSize: 22 }} />}
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', textAlign: 'center', letterSpacing: 0.4 }}>{statusText}</Typography>

          {/* Mic button / stop */}
          {srSupported ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {isListening ? (
                <Button variant="contained" startIcon={<StopIcon />} onClick={stopListening}
                  sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, borderRadius: 2.5, px: 3, textTransform: 'none', fontWeight: 700 }}>
                  Done speaking
                </Button>
              ) : (
                <Button variant="contained" startIcon={<MicIcon />} onClick={startListening}
                  disabled={isThinking || isSpeaking}
                  sx={{ bgcolor: isThinking || isSpeaking ? '#1e293b' : '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2.5, px: 3, textTransform: 'none', fontWeight: 700 }}>
                  Tap to speak
                </Button>
              )}
              {isSpeaking && (
                <Button size="small" onClick={() => { synth.cancel(); setIsSpeaking(false); }}
                  sx={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'none', border: '1px solid #1e293b', borderRadius: 2 }}>
                  Skip
                </Button>
              )}
            </Box>
          ) : (
            // Fallback text input
            <Box sx={{ display: 'flex', gap: 1, width: '100%', maxWidth: 500 }}>
              <TextField
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleTextSubmit()}
                placeholder="Type your response..."
                size="small"
                multiline
                maxRows={3}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: '#1e293b', borderRadius: 2, color: '#e2e8f0', '& fieldset': { borderColor: '#334155' } } }}
              />
              <IconButton onClick={handleTextSubmit} disabled={!textInput.trim() || isThinking}
                sx={{ bgcolor: '#6366f1', color: '#fff', borderRadius: 2, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: '#1e293b', color: '#475569' } }}>
                <CheckIcon />
              </IconButton>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

// ─── Setup view ────────────────────────────────────────────────────────────────
function SetupView({ onStart, initialSession, onSaveSetup }) {
  const [title, setTitle] = useState(initialSession?.title || initialSession?.name || '');
  const [interviewType, setInterviewType] = useState(initialSession?.interview_type || 'Behavioural');
  const [contextDocs, setContextDocs] = useState(initialSession?.context_docs || []);
  const [provider, setProvider] = useState(initialSession?.provider || 'anthropic');
  const [model, setModel] = useState(initialSession?.model || 'claude-sonnet-4-6');
  const [docDialog, setDocDialog] = useState(false);
  const [docLabel, setDocLabel] = useState('');
  const [docContent, setDocContent] = useState('');
  const [docType, setDocType] = useState('text');
  const [saving, setSaving] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);
  const [setupSavedName, setSetupSavedName] = useState('');
  const [showSaveSetupInput, setShowSaveSetupInput] = useState(false);

  const handleProviderChange = (p) => {
    setProvider(p);
    setModel(PROVIDERS[p].models[0].id);
  };

  const addDoc = () => {
    if (!docLabel.trim() || !docContent.trim()) return;
    setContextDocs(prev => [...prev, { id: crypto.randomUUID(), label: docLabel.trim(), content: docContent.trim(), type: docType }]);
    setDocLabel(''); setDocContent(''); setDocType('text'); setDocDialog(false);
  };

  const handleStart = async () => {
    setSaving(true);
    try {
      await onStart({ title: title || 'Mock Interview', interview_type: interviewType, context_docs: contextDocs, provider, model });
    } finally { setSaving(false); }
  };

  const typeMeta = INTERVIEW_TYPES.find(t => t.value === interviewType);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, sm: 4 }, maxWidth: 760, mx: 'auto', width: '100%' }}>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--tx-hi)', mb: 0.5 }}>New Rehearsal</Typography>
      <Typography sx={{ fontSize: '0.82rem', color: 'var(--tx-gh)', mb: 3.5 }}>Set up your mock interview — add context documents, pick an interview type, then start.</Typography>

      {/* Title */}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.75 }}>Session Title</Typography>
      <TextField value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Amazon Loop — Staff SDE at Stripe" fullWidth size="small"
        sx={{ mb: 3, '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-surf)', borderRadius: 1.5 } }} />

      {/* Interview type */}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>Interview Type</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1, mb: 3.5 }}>
        {INTERVIEW_TYPES.map(type => {
          const sel = interviewType === type.value;
          return (
            <Box key={type.value} onClick={() => setInterviewType(type.value)}
              sx={{ p: 1.5, borderRadius: 2, border: `2px solid ${sel ? type.color : 'var(--bd-lo)'}`, bgcolor: sel ? `${type.color}10` : 'var(--bg-surf)',
                cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: type.color, bgcolor: `${type.color}08` } }}>
              <Typography sx={{ fontSize: '1.1rem', mb: 0.4 }}>{type.emoji}</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: sel ? type.color : 'var(--tx-md)', mb: 0.3 }}>{type.value}</Typography>
              {type.company && <Chip label={type.company} size="small" sx={{ height: 14, fontSize: '0.52rem', fontWeight: 700, bgcolor: `${type.color}18`, color: type.color, '& .MuiChip-label': { px: 0.6 } }} />}
              <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)', mt: 0.5, lineHeight: 1.4 }}>{type.description}</Typography>
            </Box>
          );
        })}
      </Box>

      {/* Context documents */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Context Documents</Typography>
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />} onClick={() => setDocDialog(true)}
          sx={{ fontSize: '0.7rem', color: '#6366f1', textTransform: 'none', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 1.5, px: 1.25 }}>
          Add
        </Button>
      </Box>
      {contextDocs.length === 0 ? (
        <Box sx={{ py: 2.5, borderRadius: 2, border: '1px dashed var(--bd-lo)', textAlign: 'center', mb: 3.5 }}>
          <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-gh)' }}>No documents yet. Add a JD, links, or any relevant context.</Typography>
        </Box>
      ) : (
        <Stack spacing={0.75} sx={{ mb: 3.5 }}>
          {contextDocs.map(doc => (
            <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: 'var(--bg-surf)', borderRadius: 1.5, border: '1px solid var(--bd-lo)' }}>
              {doc.type === 'link' ? <LinkIcon sx={{ fontSize: 14, color: '#6366f1', flexShrink: 0 }} /> : <ArticleIcon sx={{ fontSize: 14, color: '#6366f1', flexShrink: 0 }} />}
              <Typography sx={{ flex: 1, fontSize: '0.78rem', color: 'var(--tx-md)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label}</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)', flexShrink: 0 }}>{doc.content.length > 60 ? doc.content.slice(0, 60) + '…' : doc.content}</Typography>
              <IconButton size="small" onClick={() => setContextDocs(prev => prev.filter(d => d.id !== doc.id))} sx={{ p: 0.25, color: 'var(--tx-ft)', '&:hover': { color: '#ef4444' } }}>
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* LLM provider + model */}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1.25 }}>AI Model</Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 3.5 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel sx={{ fontSize: '0.78rem', color: 'var(--tx-dim)' }}>Provider</InputLabel>
          <Select value={provider} label="Provider" onChange={e => handleProviderChange(e.target.value)} sx={selectSx}>
            {Object.entries(PROVIDERS).map(([key, p]) => (
              <MenuItem key={key} value={key} sx={{ fontSize: '0.82rem' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
                  <span>{p.label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1.5 }}>
          <InputLabel sx={{ fontSize: '0.78rem', color: 'var(--tx-dim)' }}>Model</InputLabel>
          <Select value={model} label="Model" onChange={e => setModel(e.target.value)} sx={selectSx}>
            {PROVIDERS[provider].models.map(m => (
              <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.82rem' }}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Save as Setup */}
      {onSaveSetup && (
        <Box sx={{ mb: 2 }}>
          {!showSaveSetupInput ? (
            <Button fullWidth variant="outlined" size="small"
              onClick={() => { setSetupSavedName(title || ''); setShowSaveSetupInput(true); }}
              sx={{ borderColor: 'var(--bd-lo)', color: 'var(--tx-dim)', textTransform: 'none', fontSize: '0.78rem', borderRadius: 1.5, '&:hover': { borderColor: '#6366f1', color: '#818cf8' } }}>
              💾 Save as Setup
            </Button>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" placeholder="Setup name…" value={setupSavedName} onChange={e => setSetupSavedName(e.target.value)}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.8rem', borderRadius: 1.5 } }} />
              <Button size="small" variant="contained" disabled={savingSetup}
                onClick={async () => {
                  setSavingSetup(true);
                  await onSaveSetup({ name: setupSavedName || title || 'Untitled Setup', interview_type: interviewType, context_docs: contextDocs, provider, model });
                  setSavingSetup(false); setShowSaveSetupInput(false);
                }}
                sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', fontSize: '0.78rem', borderRadius: 1.5, flexShrink: 0 }}>
                {savingSetup ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
              </Button>
              <IconButton size="small" onClick={() => setShowSaveSetupInput(false)} sx={{ color: 'var(--tx-dim)', p: 0.4 }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Stack>
          )}
        </Box>
      )}

      <Button variant="contained" fullWidth onClick={handleStart} disabled={saving}
        sx={{ py: 1.5, fontWeight: 700, fontSize: '0.9rem', borderRadius: 2, bgcolor: typeMeta?.color || '#6366f1', '&:hover': { bgcolor: typeMeta?.color || '#6366f1', opacity: 0.9 }, textTransform: 'none' }}>
        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '🎙 Start Interview'}
      </Button>

      {/* Add doc dialog */}
      <Dialog open={docDialog} onClose={() => setDocDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--tx-hi)', pb: 1 }}>Add Context Document</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Stack spacing={1.5}>
            <TextField label="Label" value={docLabel} onChange={e => setDocLabel(e.target.value)} size="small" fullWidth placeholder="e.g. Job Description" />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {['text', 'link'].map(t => (
                <Chip key={t} label={t === 'text' ? '📄 Text / Paste' : '🔗 URL / Link'} onClick={() => setDocType(t)}
                  sx={{ cursor: 'pointer', bgcolor: docType === t ? 'rgba(99,102,241,0.15)' : 'var(--bg-card)', color: docType === t ? '#818cf8' : 'var(--tx-gh)', border: `1px solid ${docType === t ? '#6366f1' : 'var(--bd-lo)'}`, fontWeight: docType === t ? 700 : 400 }} />
              ))}
            </Box>
            <TextField label={docType === 'link' ? 'URL' : 'Content'} value={docContent} onChange={e => setDocContent(e.target.value)} size="small" fullWidth multiline={docType === 'text'} minRows={docType === 'text' ? 5 : 1} placeholder={docType === 'link' ? 'https://...' : 'Paste text here...'} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDocDialog(false)} sx={{ color: 'var(--tx-gh)', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={addDoc} disabled={!docLabel.trim() || !docContent.trim()} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function RehearsalsPage() {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [sessions, setSessions] = useState([]);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'setup' | 'interview'
  const [activeSession, setActiveSession] = useState(null);
  const [activeSetup, setActiveSetup] = useState(null); // setup to pre-fill form
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarTab, setSidebarTab] = useState('sessions'); // 'sessions' | 'setups'
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteSetupDialog, setDeleteSetupDialog] = useState(null);

  useEffect(() => {
    loadSessions();
    loadSetups();
  }, []);

  const loadSessions = async () => {
    try {
      const res = await api.get('/api/rehearsals');
      setSessions(res.data.sessions || []);
    } catch {} finally { setLoading(false); }
  };

  const loadSetups = async () => {
    try {
      const res = await api.get('/api/rehearsals/setups');
      setSetups(res.data.setups || []);
    } catch {}
  };

  const handleSaveSetup = async (setupData) => {
    try {
      const res = await api.post('/api/rehearsals/setups', setupData);
      setSetups(prev => [res.data.setup, ...prev]);
      setSidebarTab('setups');
    } catch {}
  };

  const handleDeleteSetup = async (id) => {
    try {
      await api.delete(`/api/rehearsals/setups/${id}`);
      setSetups(prev => prev.filter(s => s.id !== id));
    } catch {}
    setDeleteSetupDialog(null);
  };

  const handleStart = async (setup) => {
    const res = await api.post('/api/rehearsals', setup);
    const session = res.data.session;
    setSessions(prev => [{ id: session.id, title: session.title, interview_type: session.interview_type, status: 'active', turn_count: 0, created_at: session.created_at, updated_at: session.updated_at }, ...prev]);
    setActiveSession(session);
    setView('interview');
    if (isMobile) setSidebarOpen(false);
  };

  const handleSessionEnd = (transcript) => {
    setSessions(prev => prev.map(s => s.id === activeSession.id ? { ...s, status: 'completed', turn_count: transcript.length } : s));
    setView('list');
    setActiveSession(null);
    setSidebarOpen(true);
  };

  const handleOpenSession = async (sessionId) => {
    try {
      const res = await api.get(`/api/rehearsals/${sessionId}`);
      setActiveSession(res.data.session);
      setView('interview');
      if (isMobile) setSidebarOpen(false);
    } catch {}
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/rehearsals/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeSession?.id === id) { setActiveSession(null); setView('list'); }
    } catch {}
    setDeleteDialog(null);
  };

  const typeColor = (type) => INTERVIEW_TYPES.find(t => t.value === type)?.color || '#64748b';

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 52px)', bgcolor: 'var(--bg-base)', overflow: 'hidden' }}>
      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && view !== 'list' && (
        <Box onClick={() => setSidebarOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 1199, bgcolor: 'rgba(0,0,0,0.5)' }} />
      )}

      {/* Sidebar */}
      {(sidebarOpen || !isMobile) && (
        <Box sx={{
          width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderRight: '1px solid var(--bd-lo)', bgcolor: 'var(--bg-surf)',
          ...(isMobile && view !== 'list' ? { position: 'fixed', top: 52, left: 0, height: 'calc(100vh - 52px)', zIndex: 1200 } : {}),
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid var(--bd-lo)' }}>
            <Typography sx={{ flex: 1, fontWeight: 800, fontSize: '0.85rem', color: 'var(--tx-hi)', letterSpacing: 0.3 }}>Rehearsals</Typography>
            <Tooltip title="New rehearsal">
              <IconButton size="small" onClick={() => { setActiveSession(null); setActiveSetup(null); setView('setup'); if (isMobile) setSidebarOpen(false); }}
                sx={{ color: '#6366f1', bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(99,102,241,0.2)' } }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Tabs */}
          <Box sx={{ display: 'flex', borderBottom: '1px solid var(--bd-lo)' }}>
            {['sessions', 'setups'].map(tab => (
              <Box key={tab} onClick={() => setSidebarTab(tab)}
                sx={{ flex: 1, py: 0.75, textAlign: 'center', cursor: 'pointer',
                  borderBottom: sidebarTab === tab ? '2px solid #6366f1' : '2px solid transparent',
                  bgcolor: sidebarTab === tab ? 'rgba(99,102,241,0.06)' : 'transparent',
                  transition: 'all 0.15s', '&:hover': { bgcolor: 'rgba(99,102,241,0.04)' } }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: sidebarTab === tab ? 700 : 500, color: sidebarTab === tab ? '#818cf8' : 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {tab === 'sessions' ? `Sessions` : `Setups`}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {/* Sessions tab */}
            {sidebarTab === 'sessions' && (loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={22} sx={{ color: '#6366f1' }} /></Box>
            ) : sessions.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 6, px: 2 }}>
                <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.82rem' }}>No rehearsals yet</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setView('setup')}
                  sx={{ mt: 1, color: '#6366f1', textTransform: 'none', fontSize: '0.78rem' }}>Create first</Button>
              </Box>
            ) : sessions.map(s => (
              <Box key={s.id} onClick={() => handleOpenSession(s.id)}
                sx={{ px: 2, py: 1.25, cursor: 'pointer', borderBottom: '1px solid var(--bd-lo)',
                  borderLeft: activeSession?.id === s.id ? `3px solid ${typeColor(s.interview_type)}` : '3px solid transparent',
                  bgcolor: activeSession?.id === s.id ? 'var(--bg-card)' : 'transparent',
                  '&:hover': { bgcolor: 'var(--bg-hover)' }, '&:hover .del-btn': { opacity: 1 },
                  position: 'relative', transition: 'all 0.15s' }}>
                <Stack direction="row" alignItems="flex-start" spacing={0.75} mb={0.35}>
                  <Typography sx={{ flex: 1, fontSize: '0.78rem', fontWeight: 600, color: 'var(--tx-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</Typography>
                  {s.status === 'completed' && <CheckIcon sx={{ fontSize: 12, color: '#10b981', flexShrink: 0 }} />}
                </Stack>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: typeColor(s.interview_type), flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.interview_type}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-dim)', flexShrink: 0 }}>· {timeAgo(s.updated_at)}</Typography>
                </Stack>
                <IconButton className="del-btn" size="small"
                  onClick={ev => { ev.stopPropagation(); setDeleteDialog(s.id); }}
                  sx={{ position: 'absolute', top: 6, right: 4, opacity: 0, p: 0.2, color: 'var(--tx-ft)', transition: 'opacity 0.15s', '&:hover': { color: '#ef4444' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )))}

            {/* Setups tab */}
            {sidebarTab === 'setups' && (setups.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 6, px: 2 }}>
                <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.82rem' }}>No saved setups yet</Typography>
                <Typography sx={{ color: 'var(--tx-dim)', fontSize: '0.72rem', mt: 0.5 }}>Use "Save as Setup" when configuring an interview</Typography>
              </Box>
            ) : setups.map(s => (
              <Box key={s.id}
                onClick={() => { setActiveSetup(s); setView('setup'); if (isMobile) setSidebarOpen(false); }}
                sx={{ px: 2, py: 1.25, cursor: 'pointer', borderBottom: '1px solid var(--bd-lo)',
                  borderLeft: activeSetup?.id === s.id ? `3px solid ${typeColor(s.interview_type)}` : '3px solid transparent',
                  bgcolor: activeSetup?.id === s.id ? 'var(--bg-card)' : 'transparent',
                  '&:hover': { bgcolor: 'var(--bg-hover)' }, '&:hover .del-btn': { opacity: 1 },
                  position: 'relative', transition: 'all 0.15s' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--tx-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.3 }}>{s.name}</Typography>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: typeColor(s.interview_type), flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)' }}>{s.interview_type}</Typography>
                </Stack>
                <IconButton className="del-btn" size="small"
                  onClick={ev => { ev.stopPropagation(); setDeleteSetupDialog(s.id); }}
                  sx={{ position: 'absolute', top: 6, right: 4, opacity: 0, p: 0.2, color: 'var(--tx-ft)', transition: 'opacity 0.15s', '&:hover': { color: '#ef4444' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )))}
          </Box>
        </Box>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {view === 'list' && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: '2rem' }}>🎙</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--tx-hi)' }}>Ready to rehearse?</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'var(--tx-gh)', textAlign: 'center', maxWidth: 340 }}>Add context documents, pick an interview type, and practice with an AI interviewer — voice only.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setView('setup')}
              sx={{ mt: 1, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
              New Rehearsal
            </Button>
          </Box>
        )}
        {view === 'setup' && (
          <SetupView onStart={handleStart} initialSession={activeSetup} onSaveSetup={handleSaveSetup} />
        )}
        {view === 'interview' && activeSession && (
          <InterviewView session={activeSession} onEnd={handleSessionEnd} />
        )}
      </Box>

      {/* Delete session confirmation */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--tx-hi)' }}>Delete rehearsal?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'var(--tx-lo)', fontSize: '0.85rem' }}>This will permanently delete the session and transcript.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteDialog(null)} sx={{ color: 'var(--tx-gh)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteDialog)} sx={{ color: '#ef4444', textTransform: 'none', fontWeight: 700 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete setup confirmation */}
      <Dialog open={!!deleteSetupDialog} onClose={() => setDeleteSetupDialog(null)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--tx-hi)' }}>Delete setup?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'var(--tx-lo)', fontSize: '0.85rem' }}>This will permanently delete this saved setup.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteSetupDialog(null)} sx={{ color: 'var(--tx-gh)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => handleDeleteSetup(deleteSetupDialog)} sx={{ color: '#ef4444', textTransform: 'none', fontWeight: 700 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
